import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  Tooltip
} from '@material-ui/core';
import {
  GetApp as ExportIcon,
  Publish as ImportIcon,
  Add as AddIcon
} from '@material-ui/icons';
import { DataGrid } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { NavLink } from 'react-router-dom';
import useApi from 'src/api/api';
import ClassSelector from 'src/components/ClassSelector';
import SearchInput from 'src/components/SearchInput';
import { SubjectSelector } from 'src/components/SubjectSelector';
import Icons from 'src/icons';
import LayoutContainer from 'src/layout/LayoutContainer';
import { useLocale } from 'src/provider/LocaleProvider';
import { useSnack } from 'src/provider/SnackbarProvider';
import { PreviewElement } from './PreviewElement';
import { ResourceCreateDialog } from './ResourceActionDialog';
import DataTable from 'src/components/DataTable/DataTable';
import utils from 'src/utils/utils';

const _ = require('lodash');
const moment = require('moment-timezone');
const prettyBytes = require('pretty-bytes');
export const LearningResource = () => {
  const { t, getLanguage } = useLocale();
  const api = useApi();
  const snack = useSnack();
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 5,
    page: 0
  });
  const [dialogMode, setDialogMode] = useState('');
  const [filter, setFilter] = useState();
  const [subject, setSubject] = useState('ALL');
  const [name, setName] = useState('');
  const [selectedRow, setSelectedRow] = useState({});
  const isOpen = useMemo(
    () => dialogMode === 'CREATE' || dialogMode === 'EDIT',
    [dialogMode]
  );

  const { data, isLoading } = useQuery(
    [
      'learning-resource',
      paginationModel.page + 1,
      paginationModel.pageSize,
      filter,
      subject,
      name
    ],
    async () => {
      const { data } = await api.getResources({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        filters: filter?._id ? [filter] : [],
        subjects: subject === 'ALL' ? [] : [subject],
        name
      });
      return data;
    }
  );
  const storagePecentage =
    (data?.storage?.usedStorage / data?.storage?.totalStorage) * 100;
  const totalStorage = prettyBytes(data?.storage?.totalStorage ?? 0);
  const usedStorage = prettyBytes(data?.storage?.usedStorage ?? 0);
  const chipColor =
    storagePecentage > 80
      ? 'error'
      : storagePecentage > 60
      ? 'warning'
      : 'primary';

  const rows = useMemo(() => {
    if (!data?.resources) return [];
    return _.map(data.resources, (row) => ({
      id: row._id || row.id,
      ...row
    }));
  }, [data]);

  const columns = [
    {
      field: 'order',
      headerName: t('order_number'),
      flex: 0.5,
      type: 'number',
      valueGetter: (params) => params.row?.order ?? 0
    },
    {
      field: 'entry_point',
      headerName: '',
      flex: 1,
      minWidth: 150,
      maxWidth: 250,
      sortable: false,
      renderCell: (params) => {
        const ext = params?.row?.file_extension;
        return <PreviewElement params={params} ext={ext} />;
      }
    },
    {
      field: 'name',
      headerName: t('name'),
      flex: 1,
      valueGetter: (params) => params.row?.name[getLanguage()]
    },
    {
      field: 'created_by',
      headerName: t('action_created_by'),
      flex: 1,
      valueGetter: (params) => params.row?.created_by?.name[getLanguage()]
    },
    {
      field: 'created_at',
      headerName: t('created_at'),
      flex: 1,
      valueGetter: (params) =>
        moment(params.value).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      field: 'updated_at',
      headerName: t('Last_Updated_at'),
      flex: 1,
      valueGetter: (params) =>
        moment(params.value).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      field: '',
      headerName: t('actions'),
      flex: 1,
      renderCell: (params) => {
        return (
          <Box sx={{ mx: -1 }}>
            <Tooltip title={t('edit')}>
              <IconButton onClick={() => handleEdit(params)} sx={{ p: 0.5 }}>
                <Icons.EditIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    }
  ];

  const handleEdit = (params) => {
    const { row } = params;
    setSelectedRow({
      ...row
    });

    setDialogMode('EDIT');
  };

  const handleCreate = () => {
    setSelectedRow();
    setDialogMode('CREATE');
  };

  const handleExport = async () => {
    try {
      // Get all resources without pagination for export
      const { data } = await api.getResources({
        page: 1,
        limit: 10000, // Large number to get all resources
        filters: [],
        subjects: [],
        name: ''
      });

      if (!data?.resources || data.resources.length === 0) {
        snack.error(t('no_data_to_export'));
        return;
      }

      // Sort resources by order
      const sortedResources = _.orderBy(data.resources, ['order'], ['asc']);

      // Convert resources to the format expected by utils.exportCsv
      const currentResourcesData = sortedResources.map((resource) => ({
        id: resource._id || resource.id,
        name_en: resource.name?.en || '',
        name_zh: resource.name?.zh || '',
        filters_name:
          resource.filters
            ?.map((f) => f.name?.en || f.name?.zh || f.value || f._id)
            .join(', ') || '',
        subjects_name:
          resource.subjects
            ?.map((s) => s.name?.en || s.name?.zh || s.name || s._id)
            .join(', ') || '',
        order: resource.order || 0
      }));

      // Use utils.exportCsv for consistent export format
      utils.exportCsv(
        currentResourcesData,
        [
          { label: 'Order', value: (row) => row.order },
          { label: 'Resource_ID', value: (row) => row.id },
          { label: 'Name_EN', value: (row) => row.name_en },
          { label: 'Name_ZH', value: (row) => row.name_zh },
          { label: 'Filters_Name', value: (row) => row.filters_name },
          { label: 'Subjects_Name', value: (row) => row.subjects_name }    
        ],
        'learning_resources_data'
      );

      snack.open(t('export_success'));
    } catch (error) {
      console.error('Export error:', error);
      snack.error(t('export_failed'));
    }
  };

  const deboundedSearch = _.debounce((value) => {
    setName(value);
  }, 500);

  return (
    <LayoutContainer title={t('learning_resource')}>
      <Grid container spacing={2} mb={2}>
        {/* Filter Section */}
        <Grid item xs={12}>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="flex-start"
            wrap="wrap"
          >
            <Grid item xs={12} md={3} lg={3}>
              <SearchInput
                setSearch={deboundedSearch}
                sx={{ height: '56px', width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} md={3} lg={3}>
              <ClassSelector
                selected={filter}
                setSelected={setFilter}
                fullWidth
                sx={{ minWidth: 0, height: '56px' }}
              />
            </Grid>
            <Grid item xs={12} md={3} lg={3}>
              <SubjectSelector
                selected={subject}
                setSelected={setSubject}
                fullWidth
                sx={{ minWidth: 0, height: '56px' }}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Button Section */}
        <Grid item xs={12}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={2}>
              <Button
                onClick={handleExport}
                variant="contained"
                fullWidth
                size="medium"
                startIcon={<ExportIcon />}
                sx={{
                  backgroundColor: '#1976d2',
                  '&:hover': { backgroundColor: '#1565c0' },
                  fontWeight: 600
                }}
              >
                {t('export')}
              </Button>
            </Grid>
            <Grid item xs={6} md={2}>
              <NavLink
                to="/admin/learningresource/upload"
                style={{ textDecoration: 'none' }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  size="medium"
                  startIcon={<ImportIcon />}
                  sx={{
                    backgroundColor: '#2e7d32',
                    '&:hover': { backgroundColor: '#1b5e20' },
                    fontWeight: 600
                  }}
                >
                  {t('import')}
                </Button>
              </NavLink>
            </Grid>
            <Grid item xs={6} md={2}>
              <Button
                onClick={handleCreate}
                variant="contained"
                fullWidth
                size="medium"
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: '#ed6c02',
                  '&:hover': { backgroundColor: '#e65100' },
                  fontWeight: 600
                }}
              >
                {t('add_learning_resource')}
              </Button>
            </Grid>
            <Grid item xs={6} md={2}>
              <Tooltip
                title={`${t(
                  'learning_resource_storage'
                )}: ${usedStorage}/${totalStorage}`}
              >
                <Chip
                  icon={<Icons.SdStorageIcon />}
                  color={chipColor}
                  label={`${storagePecentage}%`}
                  size="medium"
                  sx={{
                    height: '36px',
                    minHeight: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1rem',
                    padding: '0 16px',
                    width: '100%'
                  }}
                />
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Card sx={{ minHeight: 220, minWidth: 300 }}>
        {rows.length > 0 ? (
          <DataGrid
            rowHeight={220}
            rows={rows}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={[5, 10, 15]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            paginationMode="server"
            rowCount={data?.totalPages * paginationModel?.pageSize || 0}
            initialState={{
              sorting: {
                sortModel: [{ field: 'order', sort: 'asc' }]
              },
              columns: {
                columnVisibilityModel: {
                  order: false // Hide the order column
                }
              }
            }}
          />
        ) : (
          <DataTable />
        )}
      </Card>

      {isOpen && (
        <ResourceCreateDialog
          dialogMode={dialogMode}
          setDialogMode={setDialogMode}
          isEdit={dialogMode === 'EDIT'}
          isOpen={isOpen}
          selectedRow={selectedRow}
        />
      )}
    </LayoutContainer>
  );
};
